import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PkiMailDispatchButton · Trigger-Button + Dialog für den Mail-Versand
 * von Wochenbriefing oder Monatsreport an eine beliebige Adresse.
 *
 * Props:
 *   variant · "wochenbriefing" | "monatsreport"
 *   weeks?  · nur für Monatsreport (default 4)
 *   defaultEmail? · vorausgefüllte E-Mail
 *   testidPrefix? · z. B. "wochenbriefing" oder "monatsreport"
 */
export default function PkiMailDispatchButton({
  variant,
  weeks = 4,
  defaultEmail = "",
  testidPrefix,
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);

  const isMonats = variant === "monatsreport";
  const path = isMonats ? "monatsreport" : "wochenbriefing";
  const labelTitle = isMonats ? "Monatsreport per E-Mail senden" : "Montagsbriefing per E-Mail senden";
  const labelButton = isMonats ? "Monatsreport per Mail senden" : "Briefing per Mail senden";
  const testId = testidPrefix || path;

  const submit = async () => {
    const to = (email || "").trim().toLowerCase();
    if (!to.includes("@") || to.length < 5) {
      toast.error("Bitte gültige E-Mail-Adresse eingeben.");
      return;
    }
    setBusy(true);
    try {
      const body = isMonats ? { to_email: to, weeks } : { to_email: to };
      await axios.post(`${API}/pflege-ki/${path}/send-mail`, body, { withCredentials: true });
      toast.success(`Mail versendet an ${to}`);
      setOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Mailversand fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`${testId}-mail-open`}
          className="border-clay-dark/40 text-clay-dark"
        >
          <Mail className="h-3.5 w-3.5 mr-1.5" /> Per Mail senden
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`${testId}-mail-dialog`}>
        <DialogHeader>
          <DialogTitle>{labelTitle}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Label htmlFor="mail-to" className="text-[12.5px] text-sand-700">
            Empfänger-E-Mail
          </Label>
          <Input
            id="mail-to"
            type="email"
            placeholder="pdl@meine-einrichtung.de"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid={`${testId}-mail-input`}
            autoFocus
          />
          <p className="text-[12px] text-sand-500 leading-snug">
            Die Mail enthält die aktuellen Daten (Live-Snapshot zum Zeitpunkt des Versands).
            Audit wird im System protokolliert.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} data-testid={`${testId}-mail-cancel`}>
            Abbrechen
          </Button>
          <Button onClick={submit} disabled={busy} data-testid={`${testId}-mail-submit`}>
            {busy ? "Wird gesendet …" : (
              <><Send className="h-3.5 w-3.5 mr-1.5" /> {labelButton}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
